import {
  PE_SETTINGS,
  PE_AUTOMATION_RULES,
  PE_BROADCASTS,
  PE_CONVERSATIONS,
  CLEAR_PATIENT_ENGAGEMENT_ERRORS,
} from '../actions/patientEngagement';

// One slice for all Patient Engagement state (per-resource key families,
// recapture idiom): settings, automation rules, broadcasts, conversations.
const initialState = {
  // Settings (the GET/PATCH /api/v1/patient_engagement/settings object)
  settings: null,
  settingsLoading: false,
  settingsError: null,
  settingsSaving: false,
  settingsSaveError: null,

  // Automation rules (flat array; the page groups by kind)
  automationRules: [],
  automationRulesLoading: false,
  automationRulesError: null,
  automationRuleSaving: false,
  automationRuleSaveError: null,
  automationRuleDeleting: false,
  automationRuleDeleteError: null,
  automationRuleReordering: false,
  automationRuleReorderError: null,
  // No templatePreview here, and PE_AUTOMATION_RULES.PREVIEW_* are dispatched by
  // crud() but intentionally unhandled below: the rule editor's useRulePreview
  // hook owns that request's result, including holding the last good render
  // across a failure. Mirroring it into the store gave one request two owners
  // and left the store copy with no readers at all.

  // Broadcasts
  broadcasts: [],
  broadcastsMeta: null,
  broadcastsLoading: false,
  broadcastsError: null,
  currentBroadcast: null,
  currentBroadcastLoading: false,
  currentBroadcastError: null,
  broadcastSaving: false,
  broadcastSaveError: null,
  audiencePreview: null,
  audiencePreviewLoading: false,
  audiencePreviewError: null,
  audienceMetadata: null,
  audienceMetadataLoading: false,

  // Conversations
  conversations: [],
  unmatchedMessages: [],
  conversationsMeta: null,
  conversationsLoading: false,
  conversationsError: null,
  conversationCounts: null,
  conversationCountsLoading: false,
  currentConversation: null,
  currentTimeline: [],
  currentTimelineMeta: null,
  currentConversationLoading: false,
  currentConversationError: null,
  timelineEarlierLoading: false,
  noteSaving: false,
  noteSaveError: null,
  replySaving: false,
  replySaveError: null,
};

const extractError = (action) => {
  return action?.payload?.response?.data?.error
    || action?.payload?.response?.data?.errors?.[0]
    || 'An unexpected error occurred';
};

// A note or reply response is only allowed to affect the thread pane when it is
// still the selected thread. `meta.conversationId` is stamped by
// createConversationNote/createConversationReply; an action without it (older
// dispatch, or a hand-rolled test action) is treated as belonging to the current
// thread so behaviour degrades to the previous default rather than silently
// dropping messages.
// One page of a thread, folded into what is already on screen. Same `type-id` key
// FETCH_EARLIER_SUCCESS dedupes on, with the opposite precedence: there the rows on
// screen are the fresher ones, here the just-fetched page is. Rows the page does not
// carry keep their position, and rows it introduces are appended — correct because a
// refresh only ever fetches the page the pane is on or a LATER one, so anything new
// is chronologically after everything retained.
const timelineKey = (item) => `${item.type}-${item.id}`;

const mergeTimelinePage = (existing, fetched) => {
  if (!existing.length) return fetched;
  const incoming = new Map(fetched.map((item) => [timelineKey(item), item]));
  const merged = existing.map((item) => {
    const key = timelineKey(item);
    if (!incoming.has(key)) return item;
    const fresher = incoming.get(key);
    incoming.delete(key);
    return fresher;
  });
  return [...merged, ...incoming.values()];
};

const belongsToCurrentThread = (currentState, action) => {
  const target = action?.meta?.conversationId;
  if (target == null) return true;

  return currentState.currentConversation?.id === target;
};

export default (state, action) => {
  const currentState = state || initialState;

  switch (action.type) {
    // ============ SETTINGS ============
    case PE_SETTINGS.FETCH_BEGIN:
      return {
        ...currentState,
        settingsLoading: true,
        settingsError: null,
      };
    case PE_SETTINGS.FETCH_SUCCESS:
      return {
        ...currentState,
        settingsLoading: false,
        settings: action.payload.data.settings || null,
      };
    case PE_SETTINGS.FETCH_FAIL:
      return {
        ...currentState,
        settingsLoading: false,
        settingsError: extractError(action),
      };

    case PE_SETTINGS.UPDATE_BEGIN:
      return {
        ...currentState,
        settingsSaving: true,
        settingsSaveError: null,
      };
    case PE_SETTINGS.UPDATE_SUCCESS:
      return {
        ...currentState,
        settingsSaving: false,
        settings: action.payload.data.settings || null,
      };
    case PE_SETTINGS.UPDATE_FAIL:
      return {
        ...currentState,
        settingsSaving: false,
        settingsSaveError: extractError(action),
      };

    // ============ AUTOMATION RULES ============
    case PE_AUTOMATION_RULES.FETCH_BEGIN:
      return {
        ...currentState,
        automationRulesLoading: true,
        automationRulesError: null,
      };
    case PE_AUTOMATION_RULES.FETCH_SUCCESS:
      return {
        ...currentState,
        automationRulesLoading: false,
        automationRules: action.payload.data.rules || [],
      };
    case PE_AUTOMATION_RULES.FETCH_FAIL:
      return {
        ...currentState,
        automationRulesLoading: false,
        automationRulesError: extractError(action),
      };

    case PE_AUTOMATION_RULES.CREATE_BEGIN:
    case PE_AUTOMATION_RULES.UPDATE_BEGIN:
      return {
        ...currentState,
        automationRuleSaving: true,
        automationRuleSaveError: null,
      };
    case PE_AUTOMATION_RULES.CREATE_SUCCESS:
      return {
        ...currentState,
        automationRuleSaving: false,
        automationRules: [...currentState.automationRules, action.payload.data.rule],
      };
    case PE_AUTOMATION_RULES.UPDATE_SUCCESS:
      return {
        ...currentState,
        automationRuleSaving: false,
        automationRules: currentState.automationRules.map(
          (rule) => (rule.id === action.payload.data.rule.id ? action.payload.data.rule : rule),
        ),
      };
    case PE_AUTOMATION_RULES.CREATE_FAIL:
    case PE_AUTOMATION_RULES.UPDATE_FAIL:
      return {
        ...currentState,
        automationRuleSaving: false,
        automationRuleSaveError: extractError(action),
      };

    case PE_AUTOMATION_RULES.DELETE_BEGIN:
      return {
        ...currentState,
        automationRuleDeleting: true,
        automationRuleDeleteError: null,
      };
    case PE_AUTOMATION_RULES.DELETE_SUCCESS:
      return {
        ...currentState,
        automationRuleDeleting: false,
        automationRules: currentState.automationRules.filter(
          (rule) => rule.id !== action.payload.data.rule.id,
        ),
      };
    case PE_AUTOMATION_RULES.DELETE_FAIL:
      return {
        ...currentState,
        automationRuleDeleting: false,
        automationRuleDeleteError: extractError(action),
      };

    case PE_AUTOMATION_RULES.REORDER_BEGIN:
      return {
        ...currentState,
        automationRuleReordering: true,
        automationRuleReorderError: null,
      };
    case PE_AUTOMATION_RULES.REORDER_SUCCESS: {
      // The payload holds the freshly ordered rules for one kind; swap them in
      // for that kind and leave the other kinds untouched.
      const reordered = action.payload.data.rules || [];
      const reorderedKind = reordered.length ? reordered[0].kind : null;
      return {
        ...currentState,
        automationRuleReordering: false,
        automationRules: reorderedKind
          ? [
            ...currentState.automationRules.filter((rule) => rule.kind !== reorderedKind),
            ...reordered,
          ]
          : currentState.automationRules,
      };
    }
    case PE_AUTOMATION_RULES.REORDER_FAIL:
      return {
        ...currentState,
        automationRuleReordering: false,
        automationRuleReorderError: extractError(action),
      };

    // ============ BROADCASTS ============
    case PE_BROADCASTS.FETCH_BEGIN:
      return {
        ...currentState,
        broadcastsLoading: true,
        broadcastsError: null,
      };
    case PE_BROADCASTS.FETCH_SUCCESS:
      return {
        ...currentState,
        broadcastsLoading: false,
        broadcasts: action.payload.data.broadcasts || [],
        broadcastsMeta: action.payload.data.meta || null,
      };
    case PE_BROADCASTS.FETCH_FAIL:
      return {
        ...currentState,
        broadcastsLoading: false,
        broadcastsError: extractError(action),
      };

    case PE_BROADCASTS.FETCH_ONE_BEGIN:
      return {
        ...currentState,
        currentBroadcastLoading: true,
        currentBroadcastError: null,
      };
    case PE_BROADCASTS.FETCH_ONE_SUCCESS:
      return {
        ...currentState,
        currentBroadcastLoading: false,
        currentBroadcast: action.payload.data.broadcast || null,
      };
    case PE_BROADCASTS.FETCH_ONE_FAIL:
      return {
        ...currentState,
        currentBroadcastLoading: false,
        currentBroadcastError: extractError(action),
      };

    case PE_BROADCASTS.CREATE_BEGIN:
    case PE_BROADCASTS.UPDATE_BEGIN:
    case PE_BROADCASTS.SCHEDULE_BEGIN:
    case PE_BROADCASTS.CANCEL_BEGIN:
      return {
        ...currentState,
        broadcastSaving: true,
        broadcastSaveError: null,
      };
    case PE_BROADCASTS.CREATE_SUCCESS:
    case PE_BROADCASTS.UPDATE_SUCCESS:
    case PE_BROADCASTS.SCHEDULE_SUCCESS:
    case PE_BROADCASTS.CANCEL_SUCCESS: {
      const { broadcast } = action.payload.data;
      return {
        ...currentState,
        broadcastSaving: false,
        currentBroadcast: broadcast || currentState.currentBroadcast,
        broadcasts: currentState.broadcasts.map(
          (entry) => (broadcast && entry.id === broadcast.id ? broadcast : entry),
        ),
      };
    }
    case PE_BROADCASTS.CREATE_FAIL:
    case PE_BROADCASTS.UPDATE_FAIL:
    case PE_BROADCASTS.SCHEDULE_FAIL:
    case PE_BROADCASTS.CANCEL_FAIL:
      return {
        ...currentState,
        broadcastSaving: false,
        broadcastSaveError: extractError(action),
      };

    case PE_BROADCASTS.PREVIEW_BEGIN:
      return {
        ...currentState,
        audiencePreviewLoading: true,
        audiencePreviewError: null,
      };
    case PE_BROADCASTS.PREVIEW_SUCCESS:
      return {
        ...currentState,
        audiencePreviewLoading: false,
        audiencePreview: action.payload.data.preview || null,
      };
    case PE_BROADCASTS.PREVIEW_FAIL:
      return {
        ...currentState,
        audiencePreviewLoading: false,
        audiencePreviewError: extractError(action),
      };
    case PE_BROADCASTS.CLEAR_PREVIEW:
      return {
        ...currentState,
        audiencePreview: null,
        audiencePreviewLoading: false,
        audiencePreviewError: null,
      };

    case PE_BROADCASTS.METADATA_BEGIN:
      return {
        ...currentState,
        audienceMetadataLoading: true,
      };
    case PE_BROADCASTS.METADATA_SUCCESS:
      return {
        ...currentState,
        audienceMetadataLoading: false,
        audienceMetadata: action.payload.data || null,
      };
    case PE_BROADCASTS.METADATA_FAIL:
      // Pickers degrade to empty options; the page-level error would be noise.
      return {
        ...currentState,
        audienceMetadataLoading: false,
      };

    // ============ CONVERSATIONS ============
    case PE_CONVERSATIONS.FETCH_BEGIN:
      return {
        ...currentState,
        conversationsLoading: true,
        conversationsError: null,
      };
    case PE_CONVERSATIONS.FETCH_SUCCESS:
      // Deliberately does NOT fold the open thread's list row into
      // currentConversation: the list and the thread-detail requests run
      // concurrently on a deep link and carry no version, so an older list
      // response resolving last could paint stale opted_out_at/summary fields
      // over a newer detail. The header refreshes when the thread is reselected.
      return {
        ...currentState,
        conversationsLoading: false,
        conversations: action.payload.data.conversations || [],
        unmatchedMessages: action.payload.data.unmatched_messages || [],
        conversationsMeta: action.payload.data.meta || null,
      };
    case PE_CONVERSATIONS.FETCH_FAIL:
      return {
        ...currentState,
        conversationsLoading: false,
        conversationsError: extractError(action),
      };

    case PE_CONVERSATIONS.COUNTS_BEGIN:
      return {
        ...currentState,
        conversationCountsLoading: true,
      };
    case PE_CONVERSATIONS.COUNTS_SUCCESS:
      return {
        ...currentState,
        conversationCountsLoading: false,
        conversationCounts: action.payload.data.counts || null,
      };
    case PE_CONVERSATIONS.COUNTS_FAIL:
      // Chips render without counts; the list fetch carries the visible error.
      return {
        ...currentState,
        conversationCountsLoading: false,
      };

    case PE_CONVERSATIONS.FETCH_ONE_BEGIN:
      return {
        ...currentState,
        // A refresh of the open thread (after a sent reply) keeps the pane on screen;
        // only a selection change shows the loading state.
        currentConversationLoading: action.meta?.refresh ? currentState.currentConversationLoading : true,
        currentConversationError: null,
        // Every thread fetch is preceded by threadController.abort() in loadThread,
        // and "Load earlier" borrows that same signal — so any earlier-page request
        // in flight is already dead by the time this action is dispatched, and its
        // BEGIN can never be answered (an aborted crud request is actionless, so no
        // FETCH_EARLIER_FAIL arrives to release the flag). A selection change used to
        // hide this because it also dispatches CLEAR_CURRENT, which resets the flag;
        // the post-reply refresh does not, and left "Load earlier" stuck on
        // "Loading…" and disabled for the rest of the session — the one control that
        // reaches a patient's older history. Release it here, where the abort is.
        timelineEarlierLoading: false,
      };
    case PE_CONVERSATIONS.FETCH_ONE_SUCCESS: {
      const fetched = action.payload.data.timeline || [];
      // A selection replaces the pane wholesale. A REFRESH re-reads ONE page of a
      // thread already on screen, so replacing would throw away everything that page
      // does not carry: the earlier pages the staffer walked back to with "Load
      // earlier", and — when the reply just pushed the thread onto a new page — the
      // reply itself, which then sits only in the follow-up request and vanishes if
      // that request fails. Merge instead: the fetched rows win for anything present
      // in both (this is how a delivery badge goes sent → delivered), and rows the
      // page does not mention stay put, in place. PE timeline rows are never deleted
      // server-side, so "kept" can never mean "resurrected".
      const timeline = action.meta?.refresh
        ? mergeTimelinePage(currentState.currentTimeline, fetched)
        : fetched;
      return {
        ...currentState,
        currentConversationLoading: false,
        currentConversation: action.payload.data.conversation || null,
        currentTimeline: timeline,
        currentTimelineMeta: action.payload.data.meta || null,
      };
    }
    case PE_CONVERSATIONS.FETCH_ONE_FAIL:
      return {
        ...currentState,
        currentConversationLoading: false,
        currentConversationError: extractError(action),
      };

    case PE_CONVERSATIONS.FETCH_EARLIER_BEGIN:
      return {
        ...currentState,
        timelineEarlierLoading: true,
      };
    case PE_CONVERSATIONS.FETCH_EARLIER_SUCCESS: {
      const earlier = action.payload.data.timeline || [];
      const seen = new Set(
        currentState.currentTimeline.map((item) => `${item.type}-${item.id}`),
      );
      const fresh = earlier.filter((item) => !seen.has(`${item.type}-${item.id}`));
      return {
        ...currentState,
        timelineEarlierLoading: false,
        currentTimeline: [...fresh, ...currentState.currentTimeline],
        currentTimelineMeta: action.payload.data.meta || currentState.currentTimelineMeta,
      };
    }
    case PE_CONVERSATIONS.FETCH_EARLIER_FAIL:
      return {
        ...currentState,
        timelineEarlierLoading: false,
      };

    case PE_CONVERSATIONS.CLEAR_CURRENT:
      // The note/reply saving+error flags are cleared too: they describe an
      // in-flight write for the thread being torn down, so leaving them set shows
      // the previous patient's "Saving…"/"Sending…"/error state in the newly
      // selected patient's composer — and a stale replySaving keeps the new
      // patient's Send SMS button disabled. The in-flight POST still completes and
      // still saves/sends; its SUCCESS/FAIL is dropped below unless the author is
      // back on that thread.
      return {
        ...currentState,
        currentConversation: null,
        currentTimeline: [],
        currentTimelineMeta: null,
        currentConversationError: null,
        noteSaving: false,
        noteSaveError: null,
        // An earlier-page fetch aborted by a thread switch dispatches neither
        // SUCCESS nor FAIL (crud returns { canceled } before any lifecycle
        // action), so the flag must be released here or "Load earlier" stays
        // disabled in every thread for the rest of the session.
        timelineEarlierLoading: false,
        replySaving: false,
        replySaveError: null,
      };

    case PE_CONVERSATIONS.CREATE_NOTE_BEGIN:
      return {
        ...currentState,
        noteSaving: true,
        noteSaveError: null,
      };
    case PE_CONVERSATIONS.CREATE_NOTE_SUCCESS: {
      const { note } = action.payload.data;
      // Only touch the timeline when the note belongs to the thread on screen.
      // Without this a slow POST for patient A lands after the staff member has
      // selected patient B and renders A's clinical note inside B's thread, under
      // B's name and MRN.
      if (!belongsToCurrentThread(currentState, action)) {
        return { ...currentState, noteSaving: false };
      }
      // Re-selecting the thread refetches the timeline, which can already contain
      // this note by the time the POST resolves — dedupe rather than double-render
      // (same key shape as FETCH_EARLIER_SUCCESS).
      const alreadyPresent = currentState.currentTimeline.some(
        (item) => item.type === 'note' && note && item.id === note.id,
      );
      return {
        ...currentState,
        noteSaving: false,
        currentTimeline: alreadyPresent
          ? currentState.currentTimeline
          : [...currentState.currentTimeline, note],
      };
    }
    case PE_CONVERSATIONS.CREATE_NOTE_FAIL:
      // Same scoping for the error: surfacing A's failure in B's composer tells the
      // author the wrong patient's note failed to save.
      if (!belongsToCurrentThread(currentState, action)) {
        return { ...currentState, noteSaving: false };
      }
      return {
        ...currentState,
        noteSaving: false,
        noteSaveError: extractError(action),
      };

    case PE_CONVERSATIONS.CREATE_REPLY_BEGIN:
      return {
        ...currentState,
        replySaving: true,
        replySaveError: null,
      };
    case PE_CONVERSATIONS.CREATE_REPLY_SUCCESS: {
      const { message } = action.payload.data;
      // Only touch the timeline when the reply belongs to the thread on screen.
      // Without this a slow POST for patient A lands after the staff member has
      // selected patient B and renders A's outbound SMS inside B's thread, under
      // B's name and MRN — a message that really was texted to A.
      if (!belongsToCurrentThread(currentState, action)) {
        return { ...currentState, replySaving: false };
      }
      // Re-selecting the thread refetches the timeline, which can already contain
      // this message by the time the POST resolves — dedupe rather than
      // double-render (same key shape as CREATE_NOTE_SUCCESS).
      const alreadyPresent = currentState.currentTimeline.some(
        (item) => item.type === 'message' && message && item.id === message.id,
      );
      // Optimistically append the queued reply; the delivery badge and any PHI lock
      // reconcile on the next thread fetch (no refetch here, to avoid racing the
      // async reconcile worker).
      //
      return {
        ...currentState,
        replySaving: false,
        currentTimeline: alreadyPresent
          ? currentState.currentTimeline
          : [...currentState.currentTimeline, message],
      };
    }
    case PE_CONVERSATIONS.CREATE_REPLY_FAIL:
      // Same scoping for the error: surfacing A's send failure in B's composer
      // tells the author the wrong patient's text failed to go out.
      if (!belongsToCurrentThread(currentState, action)) {
        return { ...currentState, replySaving: false };
      }
      return {
        ...currentState,
        replySaving: false,
        replySaveError: extractError(action),
      };

    case CLEAR_PATIENT_ENGAGEMENT_ERRORS:
      return {
        ...currentState,
        settingsError: null,
        settingsSaveError: null,
        automationRulesError: null,
        automationRuleSaveError: null,
        automationRuleDeleteError: null,
        automationRuleReorderError: null,
        broadcastsError: null,
        currentBroadcastError: null,
        broadcastSaveError: null,
        audiencePreviewError: null,
        conversationsError: null,
        currentConversationError: null,
        noteSaveError: null,
        replySaveError: null,
      };

    default:
      return currentState;
  }
};
